import { Typography, useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

type SourceLabelProps = {
    text: string;
    sx?: SxProps<Theme>;
};

/**
 * SourceLabel Component
 *
 * A reusable component that displays a label
 *
 * @component
 * @param {SourceLabelProps} props - Component props
 * @param {string} props.text - The main label text displayed next to the tooltip icon.
 * @param {string} props.sx - Overwrite default styling.
 * @returns {JSX.Element} A Typography component containing an info icon with a tooltip and label text.
 *
 * @example
 * ```tsx
 * <SourceLabel tooltipText="Additional information" text="Sources:" />
 * ```
 */
function SourceLabel({ text, sx }: SourceLabelProps) {
    const theme = useTheme();
    return (
        <Typography
            variant="body1"
            sx={{
                color: theme.palette.text.primary,
                fontWeight: "600",
                marginRight: "5px",
                fontSize: "1rem",
                lineHeight: "12px",
                display: "flex",
                alignItems: "center",
                userSelect: "none",
                verticalAlign: "middle",
                ...sx
            }}
        >
            {text}
        </Typography>
    );
}

export default SourceLabel;
